import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import { EmitMessage } from '@app/embed/messages.types';
import { useApplyRootStyles, useModalPositioning, useOnAppResize } from '@app/customer/Composables/useEmbeddedApp';

const iframeHostBaseUrl = ref<string | null>(null);

export function useEmbeddedApp() {

  const $rootWindow = window.top || window.parent || window;

  const { updateIFrameHostViewport } = useModalPositioning();

  function sendToIFrame(message: EmitMessage): void {
    $rootWindow.postMessage(message, '*');
  }

  function initiateHooks() {
    // when the app resizes, we want to emit the new height to the iframe
    useOnAppResize((height: number): void => {
      sendToIFrame({ event: 'onAppResize', height: height });
    });

    // when the app navigates, we want to emit the new url to the iframe
    router.on('navigate', (event): void => {
      sendToIFrame({ event: 'onAppNavigate', url: event.detail.page.url, external: false });
    });

    window.addEventListener('message', function (message: MessageEvent<EmitMessage>): void {
      if (message.data.event === 'onIFrameInit') {
        iframeHostBaseUrl.value = message.data.iframeHostUrl;
        useApplyRootStyles(message.data.styles);
      }

      if (message.data.event === 'onIFrameHostViewportChange') {
        updateIFrameHostViewport(message.data);
      }
    });

    /**
     * We call the onAppMounted at the end of this function to notify
     * the iframe that the app is mounted and listeners are registered.
     */
    sendToIFrame({ event: 'onAppMounted' });
  }

  function navigateIFrameHostToUrl(url: string): void {
    sendToIFrame({ event: 'onAppNavigate', url: url, external: true });
  }

  function scrollToTop(): void {
    sendToIFrame({ event: 'onAppScrollToTop' });
  }

  return {
    initiateHooks,
    isEmbedded: window.self !== window.top,
    iframeHostBaseUrl,
    navigateIFrameHostToUrl,
    scrollToTop,
  };
}
